import React, { JSX } from 'react';
import styled, { useTheme } from 'styled-components/native';
import { AppTextView } from '@/components/base/app.text.view';
import { Box, BoxProps } from '@/components/base/box';
import { AppIconButton } from '@/components/base/app.icon.button';
import { AppCardView } from '@/components/base/app.card.view';
import { AppAvatar } from './app.avatar';
import { AppPressable } from '@/components/base/app.pressable';

interface Props {
  online?: boolean;
  imageUrl?: string;
  title: string;
  subtitle: string | JSX.Element;
  children?: any;
  renderImage?: () => JSX.Element;
  leftIcon?: boolean;
  onPress?: () => void;
  style?: BoxProps['style'];
}
function BaseAppListRow({ onPress, leftIcon = true, online, imageUrl, style, renderImage, title, subtitle, children }: Props) {
  const theme = useTheme();
  return (
    <Container style={style}>
      <Shadow>
        <PressableRow disabled={!onPress} onPress={onPress}>
          <Box fullWidth flexGrow={1}>
            <InnerContainer flexGrow={1}>
              {leftIcon && <LeftIcon icon={'back'} color={theme.colors.icon.jade} />}
              <TextContainers>
                <AppTextView bold variant={'medium'} textColor={'blue'} text={title} />
                {typeof subtitle === 'string' ? <AppTextView text={subtitle} variant={'primary'} textColor={'jade'} /> : subtitle}
              </TextContainers>
              <Box>
                {online && <OnlineStatus />}
                {imageUrl && <AppAvatar url={imageUrl} />}
                {renderImage && renderImage()}
              </Box>
            </InnerContainer>
            {children}
          </Box>
        </PressableRow>
      </Shadow>
    </Container>
  );
}

const Shadow = styled(AppCardView)`
  width: 100%;
  flex-grow: 1;
  border-radius: ${({ theme }) => theme.hp(1.2)}px;
  justify-content: center;
  flex-direction: column;
`;

const InnerContainer = styled(Box)`
  padding-right: 15px;
  align-items: center;
  flex-direction: row;
  padding-top: 7px;
  padding-bottom: 7px;
  flex-grow: 1;
`;

const Container = styled(Box)`
  width: ${({ theme }) => theme.wp(100)}px;
  padding-horizontal: 2.5%;
  align-items: center;
  margin-top: ${({ theme }) => theme.hp(1.2)}px;
  min-height: 110px;
`;

const TextContainers = styled(Box)`
  margin-right: 4%;
  margin-left: 4%;
  justify-content: space-between;
  flex-direction: column;
  align-items: flex-end;
  flex-grow: 1;
  flex-shrink: 1;
`;

const OnlineStatus = styled(Box)`
  width: ${({ theme }) => theme.hp(1.8)}px;
  height: ${({ theme }) => theme.hp(1.8)}px;
  background-color: ${({ theme }) => theme.colors.indicator.online};
  z-index: 4;
  border-radius: 100px;
  position: absolute;
  right: 7%;
  bottom: 3%;
  border-width: 1px;
  border-color: white;
`;

const LeftIcon = styled(AppIconButton)`
  margin-left: 15px;
`;

const PressableRow = styled(AppPressable)`
  flex-grow: 1;
`;

export const AppListRow = React.memo(BaseAppListRow);
